/**
 * Cirrus Next Matchmaker block.
 *
 * The block template turns labels into placeholders while rendering. Gravity
 * Forms repaints the form on its own for AJAX page changes and validation
 * errors, without going through the block, so the same presentation is applied
 * again here.
 *
 * @see MDS-705
 */
(function () {
	'use strict';

	const ROOT_SELECTOR = '.cirrus-next-matchmaker';
	const PLACEHOLDER_TYPES = ['text', 'email', 'tel', 'url', 'number'];

	// Labels carry the required marker as a nested span, and the pills of the
	// design hold the name of the field alone: the mark stays in the label,
	// which screen readers still read.
	function textOf(element) {
		if (!element) return '';

		const copy = element.cloneNode(true);
		copy.querySelectorAll('.gfield_required').forEach(function (marker) {
			marker.remove();
		});

		return copy.textContent.trim();
	}

	function labelFor(field) {
		return textOf(field.querySelector('.gfield_label .gform-field-label__text, .gfield_label'));
	}

	// A select has no placeholder attribute: what it shows while empty is the
	// text of its empty option, which Gravity Forms leaves blank unless the form
	// declares a placeholder. The country dropdown of the address step arrived
	// here as an empty pill because of that.
	function setPlaceholder(control, text) {
		if (!text) return;

		if (control.tagName === 'SELECT') {
			const empty = control.querySelector('option[value=""]');
			if (empty && '' === empty.textContent.trim()) {
				empty.textContent = text;
			}
			return;
		}

		if (control.placeholder) return;
		control.placeholder = text;
	}

	function applyPlaceholders(field) {
		if (field.classList.contains('gform_validation_container')) return;

		const complex = field.querySelector('.ginput_complex');
		let applied = false;

		if (complex) {
			complex.querySelectorAll('input, select').forEach(function (control) {
				applied = true;
				const subLabel = complex.querySelector('label[for="' + control.id + '"]');
				if (!subLabel) return;
				setPlaceholder(control, textOf(subLabel));
			});
		} else {
			const label = labelFor(field);
			if (!label) return;

			field.querySelectorAll('input, textarea, select').forEach(function (control) {
				if ('SELECT' !== control.tagName && control.type && PLACEHOLDER_TYPES.indexOf(control.type) === -1) return;
				applied = true;
				setPlaceholder(control, label);
			});
		}

		// Choice fields keep their visible label: it is the only thing naming
		// the group once text fields move to placeholders.
		if (applied) {
			field.classList.add('hidden_label');
		}
	}

	// On desktop the fields of a step scroll inside the panel, so a field that
	// comes back with an error can sit outside the visible part of it. Gravity
	// Forms only scrolls the window, which leaves the panel showing the step as
	// if nothing had happened.
	function revealFirstError(root) {
		const page = Array.prototype.find.call(
			root.querySelectorAll('.gform_page'),
			function (candidate) {
				return null !== candidate.offsetParent;
			}
		);

		const scroller = page ? page.querySelector('.gform_page_fields') : root.querySelector('.gform_page_fields');
		if (!scroller || scroller.scrollHeight <= scroller.clientHeight) return;

		// The message says what is wrong, and the field it belongs to can be
		// taller than the visible part of the panel, so that is what has to end
		// up in view.
		const target = scroller.querySelector('.gfield_error .gfield_validation_message, .gfield_error .validation_message')
			|| scroller.querySelector('.gfield_error');
		if (!target) return;

		const MARGIN = 16;
		const rect = target.getBoundingClientRect();
		const view = scroller.getBoundingClientRect();

		if (rect.top < view.top) {
			scroller.scrollTop += rect.top - view.top - MARGIN;
		} else if (rect.bottom > view.bottom) {
			scroller.scrollTop += rect.bottom - view.bottom + MARGIN;
		}
	}

	// The design draws the textarea two lines tall, and the panel it sits in
	// already scrolls, so instead of scrolling inside itself the box follows
	// the text from the third line on. The floor lives in the stylesheet.
	function fitToText(textarea) {
		const styles = window.getComputedStyle(textarea);
		const borders = 'border-box' === styles.boxSizing
			? parseFloat(styles.borderTopWidth) + parseFloat(styles.borderBottomWidth)
			: 0;

		// Collapsing it first is what makes the scroll height the height of the
		// text: left alone, the box reports the ten rows Gravity Forms asks for.
		// The floor of the stylesheet holds through this, since a min height
		// still applies while the height is zero.
		textarea.style.height = '0';
		textarea.style.height = (textarea.scrollHeight + borders) + 'px';
	}

	function growWithText(root) {
		root.querySelectorAll('textarea').forEach(function (textarea) {
			if ('on' === textarea.dataset.cbAutogrow) return;
			textarea.dataset.cbAutogrow = 'on';

			textarea.addEventListener('input', function () {
				fitToText(textarea);
			});

			// A step that comes back from validation brings what was typed.
			if ('' !== textarea.value && null !== textarea.offsetParent) {
				fitToText(textarea);
			}
		});
	}

	function adapt(root) {
		root.querySelectorAll('.gfield').forEach(applyPlaceholders);
		growWithText(root);
		revealFirstError(root);
	}

	function adaptAll() {
		document.querySelectorAll(ROOT_SELECTOR).forEach(adapt);
	}

	document.addEventListener('DOMContentLoaded', adaptAll);

	if (window.jQuery) {
		jQuery(document).on('gform_post_render', function () {
			adaptAll();
		});
	}
})();
